// Éléments communs de l'application terrain : en-tête de page avec retour, sections à filets,
// lignes cliquables, états en texte coloré, bloc mis en avant, définitions, état local d'un objet.
import { classes } from '@cc/ui';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useEffect, type ReactNode } from 'react';
import { Link } from 'react-router';
import { LIBELLES_ETAT_LOCAL, TONS_ETAT_LOCAL, type EtatLocalObjet, type TonLocal } from '../../terrain/etat-local';
import styles from './terrain.module.css';

export interface ProprietesEnTeteTerrain {
  titre: string;
  surTitre?: ReactNode;
  sousTitre?: ReactNode;
  retour?: string;
  libelleRetour?: string;
  actions?: ReactNode;
}

export function EnTeteTerrain({ titre, surTitre, sousTitre, retour, libelleRetour = 'Retour', actions }: ProprietesEnTeteTerrain) {
  useEffect(() => {
    document.title = `${titre} - Terrain - CollectivitésConnect`;
  }, [titre]);
  return (
    <header className={styles.entetePage}>
      {retour ? (
        <div className={styles.ligneRetour}>
          <Link to={retour} className={styles.retour} aria-label={libelleRetour}>
            <ChevronLeft aria-hidden strokeWidth={2} width={22} height={22} />
          </Link>
          <span className={styles.aide}>{libelleRetour}</span>
        </div>
      ) : null}
      {surTitre ? <p className={styles.surTitre}>{surTitre}</p> : null}
      <h1 tabIndex={-1} className={styles.titrePage}>
        {titre}
      </h1>
      {sousTitre ? <p className={styles.sousTitre}>{sousTitre}</p> : null}
      {actions ? <div className={styles.ligneBoutons}>{actions}</div> : null}
    </header>
  );
}

export function Section({ titre, children, id, droite }: { titre: string; children: ReactNode; id?: string; droite?: ReactNode }) {
  const idTitre = `${id ?? titre.toLowerCase().replace(/[^a-z0-9]+/g, '-')}-titre`;
  return (
    <section className={styles.section} aria-labelledby={idTitre}>
      <div className={styles.enteteSection}>
        <h2 id={idTitre} className={styles.titreSection}>
          {titre}
        </h2>
        {droite}
      </div>
      {children}
    </section>
  );
}

export function Liste({ children, libelle }: { children: ReactNode; libelle?: string }) {
  return (
    <ul className={styles.liste} aria-label={libelle}>
      {children}
    </ul>
  );
}

export interface ProprietesLigne {
  href?: string;
  titre: ReactNode;
  sousTitre?: ReactNode;
  droite?: ReactNode;
  ariaLabel?: string;
}

export function Ligne({ href, titre, sousTitre, droite, ariaLabel }: ProprietesLigne) {
  const contenu = (
    <>
      <span className={styles.ligneTexte}>
        <span className={styles.ligneTitre}>{titre}</span>
        {sousTitre ? <span className={styles.ligneSous}>{sousTitre}</span> : null}
      </span>
      {droite !== undefined ? <span className={styles.ligneDroite}>{droite}</span> : <span />}
      {href ? <ChevronRight className={styles.chevron} aria-hidden strokeWidth={2} width={14} height={14} /> : null}
    </>
  );
  if (href) {
    return (
      <li>
        <Link to={href} className={styles.ligne} aria-label={ariaLabel}>
          {contenu}
        </Link>
      </li>
    );
  }
  return <li className={classes(styles.ligne, styles.ligneSansChevron)}>{contenu}</li>;
}

const CLASSE_TON: Record<TonLocal, string | undefined> = { neutre: styles.etatNeutre, info: styles.etatInfo, attention: styles.etatAttention, ok: styles.etatOk };

/** État en texte coloré précédé d'un point ; le libellé porte toujours l'information. */
export function TexteEtat({ libelle, ton = 'neutre' }: { libelle: string; ton?: TonLocal }) {
  return <span className={classes(styles.etat, CLASSE_TON[ton])}>{libelle}</span>;
}

/** « Enregistré sur cet appareil », « Synchronisé », « Conflit » ; rien sans opération locale. */
export function EtatLocal({ etat }: { etat: EtatLocalObjet }) {
  if (!etat) return null;
  return <TexteEtat libelle={LIBELLES_ETAT_LOCAL[etat]} ton={TONS_ETAT_LOCAL[etat]} />;
}

export function Bloc({ etiquette, titre, children, className }: { etiquette?: string; titre?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <section className={classes(styles.bloc, className)} aria-label={typeof titre === 'string' ? titre : etiquette}>
      {etiquette ? <span className={styles.blocEtiquette}>{etiquette}</span> : null}
      {titre ? <h2 className={styles.blocTitre}>{titre}</h2> : null}
      {children}
    </section>
  );
}

export function Definitions({ elements }: { elements: { terme: string; valeur: ReactNode }[] }) {
  return (
    <dl className={styles.definitions}>
      {elements.map((e) => (
        <div key={e.terme}>
          <dt>{e.terme}</dt>
          <dd>{e.valeur}</dd>
        </div>
      ))}
    </dl>
  );
}

export function Mono({ children }: { children: ReactNode }) {
  return <span className={styles.mono}>{children}</span>;
}
